import React, { useCallback, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Info, Ban, Calendar } from "lucide-react";
import { updateLWFStatus } from "@/services/adminService";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

/* ---------------- Helper Component: State Card ---------------- */

const LwfStateCard = ({
  stateConfig,
  savingSection,
  refreshLWF,
  canEditTaxConfig,
}) => {
  const {
    state,
    enabled,
    is_lwf_applicable,
    employee_contribution,
    employer_contribution,
    deduction_cycle,
  } = stateConfig;

  // confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
  });

  // Function to format contribution amounts (handling null, decimals, and percentage)
  const formatContribution = (value) => {
    if (value === null) return "N/A";
    if (typeof value === "string" && value.includes("%")) return value;

    // Check if the number is a float (like 0.75 or 2.25) or integer (like 15 or 45)
    // Assuming decimal places are required for precision (like Delhi's contribution)
    const formattedValue = Number.isInteger(value)
      ? value.toFixed(0)
      : value.toFixed(2);

    return `₹ ${formattedValue}`;
  };

  // --- Case 1: LWF Not Applicable ---
  if (!is_lwf_applicable) {
    return (
      <Card className="p-4 border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex flex-col justify-center min-h-[160px] h-full">
        <div className="flex flex-col items-center justify-center text-center py-4">
          <Ban className="w-8 h-8 text-red-500 mb-2" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Labour Welfare Fund is not applicable for {state}.
          </p>
        </div>
      </Card>
    );
  }

  // --- Case 2: LWF Applicable (Enabled/Disabled) ---
  const isEnabled = enabled;
  const statusColor = isEnabled ? "bg-emerald-500" : "bg-orange-500";
  const textColor = isEnabled ? "text-emerald-500" : "text-orange-500";
  const statusText = isEnabled ? "Enabled" : "Disabled";

  const buttonAction = isEnabled ? (
    <Button
      variant="link"
      size="sm"
      onClick={() => {
        if (!canEditTaxConfig) return;
        handleStatusChange("Disable");
      }}
      disabled={savingSection === state || !canEditTaxConfig}
      className="text-blue-500 hover:text-blue-600 h-6 p-0"
    >
      (Disable)
    </Button>
  ) : (
    <Button
      variant="link"
      size="sm"
      onClick={() => {
        if (!canEditTaxConfig) return;
        handleStatusChange("Enable");
      }}
      disabled={savingSection === state || !canEditTaxConfig}
      className="text-blue-500 hover:text-blue-600 h-6 p-0"
    >
      (Enable)
    </Button>
  );

  const handleStatusChange = async (status) => {
    if (!canEditTaxConfig) return;
    setConfirmData({
      title: `${status} LWF for your work location(s) at ${state}?`,
    });
    setConfirmOpen(true);
  };

  const confirmStatusChange = async () => {
    if (!canEditTaxConfig) {
      toast.error("You do not have permission to modify LWF settings.");
      return;
    }

    try {
      const payload = {
        state,
        enabled,
      };
      await updateLWFStatus(payload);
      toast.success("LWF Details updated successfully!");
      await refreshLWF();
    } catch (err) {
      toast.error("Failed to update LWF status!");
    }
  };

  return (
    <Card className="p-5 h-full border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 bg-white/60 dark:bg-[#1a1c23]/60 backdrop-blur">
      {/* Header */}
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
        {state}
      </h3>

      {/* Contributions */}
      <div className="space-y-3 text-sm">
        <div className="flex justify-between items-center">
          <span className="text-gray-600 dark:text-gray-400">
            Employees' Contribution
          </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {formatContribution(employee_contribution)}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-gray-600 dark:text-gray-400">
            Employer's Contribution
          </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {formatContribution(employer_contribution)}
          </span>
        </div>

        {/* Deduction Cycle */}
        <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
          <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <Calendar className="w-4 h-4 text-[#3194A0]" />
            Deduction Cycle
          </span>
          <span className="font-semibold capitalize text-gray-900 dark:text-gray-100">
            {deduction_cycle}
          </span>
        </div>

        {/* Status */}
        <div className="flex justify-between items-center pt-2">
          <span className="font-medium text-gray-700 dark:text-gray-300">
            Status
          </span>

          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${statusColor}`}></span>
            <span className={`font-semibold ${textColor}`}>{statusText}</span>

            {/* Toggle Button */}
            {buttonAction}
          </div>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={confirmData.title}
        message={confirmData.message}
        onConfirm={confirmStatusChange}
      />
    </Card>
  );
};

/* ---------------- Main Component ---------------- */

const LwfConfiguration = ({
  lwfConfig, // This is the array of state objects from the backend
  handleEnable,
  handleDisable,
  savingSection,
  refreshLwfConfig,
  canEditTaxConfig,
}) => {
  // We use a simplified internal state for demonstration, but in a real app,
  // the handleEnable/Disable should trigger a save that updates the `lwfConfig` prop.
  const [localSaving, setLocalSaving] = useState(null);

  // Example handler functions (these should come from the parent TaxConfiguration)
  const onEnable = useCallback((stateName) => {
    // This should ultimately call the parent's handleEnable/handleSave function with the state name
    alert(`Enabling LWF for ${stateName}. This should trigger API call.`);
    // For visual feedback before prop update:
    setLocalSaving(stateName);
    setTimeout(() => {
      setLocalSaving(null);
      // Simulate local state update for demo
      // For actual implementation, the parent TaxConfiguration needs to handle the API call and reload.
    }, 1000);
  }, []);

  const onDisable = useCallback((stateName) => {
    // This should ultimately call the parent's handleDisable function with the state name
    alert(`Disabling LWF for ${stateName}. This should trigger API call.`);
    setLocalSaving(stateName);
    setTimeout(() => {
      setLocalSaving(null);
    }, 1000);
  }, []);

  if (!lwfConfig || lwfConfig.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 border rounded-lg bg-white dark:bg-card">
        <Info className="w-8 h-8 text-blue-500 mb-3" />
        <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
          No Labour Welfare Fund locations configured.
        </p>
        <p className="text-sm text-gray-500 mt-1">
          Please add work locations to enable LWF configuration.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        The Labour Welfare Fund contributions are determined by your company's
        official work locations.
      </p>

      {/* State Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {lwfConfig.map((config) => (
          <LwfStateCard
            key={config.state}
            stateConfig={config}
            // Use the parent's passed handlers or the local simplified ones
            handleEnable={handleEnable || onEnable}
            handleDisable={handleDisable || onDisable}
            savingSection={savingSection || localSaving}
            refreshLWF={refreshLwfConfig}
            canEditTaxConfig={canEditTaxConfig}
          />
        ))}
      </div>
    </div>
  );
};

export default LwfConfiguration;
